import { useRef, useState } from 'react';
import { business } from '../../../shared/config/site';
import { useScrollChoreography } from '../../../shared/hooks/useScrollChoreography';
import { motionQuery } from '../../../shared/lib/gsap';
import { writeHeroProgress } from '../../../shared/lib/scroll-store';
import { SiteHeader } from '../../site/components/SiteHeader';
import { PerimeterStage } from './PerimeterStage';

const chapters = [
  { code: 'A', title: 'The perimeter', body: 'Fence line, gate, the corner nobody looks at.' },
  { code: 'B', title: 'The signal', body: 'A panel fires at 02:14. The control room has it.' },
  { code: 'C', title: 'The response', body: 'A vehicle takes the address. Perimeter first.' },
] as const;

export function Hero() {
  const rootRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [chapter, setChapter] = useState(0);

  useScrollChoreography(rootRef, ({ gsap }) => {
    const media = gsap.matchMedia();

    media.add(motionQuery.full, () => {
      const trigger = gsap.timeline({
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top top',
          end: '+=130%',
          pin: stageRef.current,
          pinSpacing: true,
          scrub: 0.6,
          onUpdate: (self) => {
            writeHeroProgress(self.progress, chapters.length);
            setChapter(Math.min(Math.floor(self.progress * chapters.length), chapters.length - 1));
          },
        },
      });

      // The headline leaves before the chapters do, so two things the reader
      // can see at once travel through the same scroll range at two rates.
      trigger
        .to('[data-hero-headline]', { yPercent: -34, opacity: 0.12, ease: 'none' }, 0)
        .to('[data-hero-sub]', { yPercent: -18, opacity: 0, ease: 'none' }, 0)
        .to('[data-hero-chapters]', { yPercent: -6, ease: 'none' }, 0);
    });

    media.add(motionQuery.reduced, () => {
      writeHeroProgress(0.18, chapters.length);
    });

    return () => media.revert();
  });

  return (
    <section ref={rootRef} id="top" className="relative">
      <div ref={stageRef} className="relative h-[100dvh] min-h-[34rem] w-full overflow-hidden">
        <PerimeterStage />
        <SiteHeader />

        {/* Scrim: the display sits on a rendered scene, so it gets a real one. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-20"
          style={{
            background:
              'linear-gradient(to top, rgba(7,9,12,0.94) 4%, rgba(7,9,12,0.55) 42%, rgba(7,9,12,0.2) 76%)',
          }}
        />

        <div className="u-shell-padded relative z-30 flex h-full flex-col justify-end gap-0 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-[calc(var(--banner-h,3rem)+4.5rem)] sm:pb-10 sm:pt-[calc(var(--banner-h,3rem)+5rem)]">
          <p className="u-label text-[color:var(--steel-400)]">
            Jet Park · Ekurhuleni
            <span className="mx-2 text-[color:var(--signal-500)]">/</span>
            Guarding · Response · Training
          </p>

          <div className="mt-auto pt-6 sm:pt-10">
            <h1
              data-hero-headline
              className="u-balance u-display text-[color:var(--bone-50)]"
              style={{ fontSize: 'var(--display-size)', maxWidth: '12ch' }}
            >
              Your alarm reaches a person
            </h1>
            <p
              data-hero-sub
              className="mt-5 text-[color:var(--bone-200)] sm:mt-7"
              style={{ maxWidth: '42ch' }}
            >
              Guarding, armed response and training out of Jet Park, across Boksburg, Kempton Park and
              the East Rand.
            </p>
          </div>

          <div data-hero-chapters className="mt-8 flex flex-col gap-6 sm:mt-16 lg:flex-row lg:items-end lg:justify-between">
            <ol className="flex flex-col gap-3 lg:max-w-[40ch]">
              {chapters.map((item, index) => {
                const live = index === chapter;

                return (
                  // A phone shows one chapter at a time — three stacked
                  // paragraphs ran under the action dock. The choice is made in
                  // CSS, not after mount: swapping the list post-hydration moved
                  // the headline and reset its Largest Contentful Paint.
                  <li key={item.code} className={live ? 'flex gap-4' : 'hidden gap-4 sm:flex'}>
                    <span
                      className="u-label pt-1 transition-colors"
                      style={{
                        transitionDuration: 'var(--t-state)',
                        transitionTimingFunction: 'var(--e-settle)',
                        color: live ? 'var(--signal-500)' : 'var(--steel-500)',
                      }}
                    >
                      {item.code}
                    </span>
                    <span
                      className="text-base transition-colors"
                      style={{
                        transitionDuration: 'var(--t-state)',
                        transitionTimingFunction: 'var(--e-settle)',
                        color: live ? 'var(--bone-50)' : 'var(--steel-500)',
                      }}
                    >
                      <strong className="font-medium">{item.title}.</strong> {item.body}
                    </span>
                  </li>
                );
              })}
            </ol>

            <div className="hidden shrink-0 flex-col items-end gap-2 lg:flex">
              <a
                href={business.telephoneHref}
                className="u-display inline-flex items-center gap-3 bg-[color:var(--signal-500)] px-7 py-4 text-[1.05rem] text-[color:var(--ink-900)] transition-transform hover:scale-[1.02] active:scale-[0.99]"
                style={{ transitionDuration: 'var(--t-tap)', transitionTimingFunction: 'var(--e-settle)' }}
              >
                Call {business.telephoneDisplay}
              </a>
              <a
                href={business.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="u-label inline-flex min-h-[44px] items-end border-b border-[color:var(--steel-600)] pb-1 text-[color:var(--steel-400)] transition-colors hover:border-[color:var(--bone-50)] hover:text-[color:var(--bone-50)]"
                style={{ transitionDuration: 'var(--t-state)', transitionTimingFunction: 'var(--e-settle)' }}
              >
                Directions to 32 Kelly Rd
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
